import React, { useState, useEffect } from 'react';
import { Card, Tab, Tabs, Button, Modal, Form } from 'react-bootstrap';
import { BsPlusLg, BsPencil, BsTrash } from 'react-icons/bs';
import AppointmentParametersService from '../services/AppointmentParametersService';
import XPortionService from '../services/XPortionService';
import { NonOrgPositionService } from '../services/NonOrgPositionService';
import dateService from '../services/dateService';
import ResponsiveTable from './ResponsiveTable';
import { toast } from 'react-toastify';

export default function AppointmentParameters() {
    const [key, setKey] = useState('employment');
    const [employmentTypes, setEmploymentTypes] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);
    const [experienceLevels, setExperienceLevels] = useState([]);
    const [organizationalLevels, setOrganizationalLevels] = useState([]);
    const [organizationalUnitTypes, setOrganizationalUnitTypes] = useState([]);
    const [xPortions, setXPortions] = useState([]);
    const [nonOrgPositions, setNonOrgPositions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentItem, setCurrentItem] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        multiplier: '',
        date: '',
        value: '',
        description: ''
    });

    useEffect(() => {
        loadAllData();
    }, []);

    const loadAllData = async () => {
        try {
            const [empTypes, eduLevels, expLevels, orgLevels, orgTypes, xPortionValues, nonOrgPositionsData] = await Promise.all([
                AppointmentParametersService.getAllEmploymentTypes(),
                AppointmentParametersService.getAllEducationLevels(),
                AppointmentParametersService.getAllExperienceLevels(),
                AppointmentParametersService.getAllOrganizationalLevels(),
                AppointmentParametersService.getAllOrganizationalUnitTypes(),
                XPortionService.getAll(),
                NonOrgPositionService.getAll()
            ]);
            setEmploymentTypes(empTypes);
            setEducationLevels(eduLevels);
            setExperienceLevels(expLevels);
            setOrganizationalLevels(orgLevels);
            setOrganizationalUnitTypes(orgTypes);
            setXPortions(xPortionValues);
            setNonOrgPositions(nonOrgPositionsData);
        } catch (error) {
            toast.error('Failed to load data');
        }
    };

    const handleOpenModal = (mode, item = null) => {
        setModalMode(mode);
        setCurrentItem(item);
        if (item) {
            if (key === 'xportion') {
                setFormData({
                    value: item.value.toString(),
                    dateTime: item.dateTime
                });
            } else {
                setFormData({
                    name: item.name || '',
                    multiplier: item.multiplier?.toString() || '',
                    date: item.date || '',
                    value: item.value?.toString() || '',
                    description: item.description || ''
                });
            }
        } else {
            setFormData({ name: '', multiplier: '', date: '', value: '', description: '' });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentItem(null);
        setFormData({ name: '', multiplier: '', date: '', value: '', description: '' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const service = AppointmentParametersService;

            let payload = {
                name: formData.name,
                description: formData.description,
                multiplier: parseFloat(formData.multiplier)
            };

            switch (key) {
                case 'employment':
                    if (modalMode === 'add') {
                        await service.createEmploymentType(payload);
                    } else if (currentItem?.id) {
                        await service.updateEmploymentType(currentItem.id, payload);
                    }
                    const newEmpData = await service.getAllEmploymentTypes();
                    setEmploymentTypes(newEmpData);
                    break;

                case 'education':
                    if (modalMode === 'add') {
                        await service.createEducationLevel(payload);
                    } else if (currentItem?.id) {
                        await service.updateEducationLevel(currentItem.id, payload);
                    }
                    const newEduData = await service.getAllEducationLevels();
                    setEducationLevels(newEduData);
                    break;

                case 'experience':
                    if (modalMode === 'add') {
                        await service.createExperienceLevel(payload);
                    } else if (currentItem?.id) {
                        await service.updateExperienceLevel(currentItem.id, payload);
                    }
                    const newExpData = await service.getAllExperienceLevels();
                    setExperienceLevels(newExpData);
                    break;

                case 'organizational':
                    if (modalMode === 'add') {
                        await service.createOrganizationalLevel(payload);
                    } else if (currentItem?.id) {
                        await service.updateOrganizationalLevel(currentItem.id, payload);
                    }
                    const newOrgLevelData = await service.getAllOrganizationalLevels();
                    setOrganizationalLevels(newOrgLevelData);
                    break;

                case 'unit':
                    if (modalMode === 'edit' && currentItem?.id) {
                        await service.updateOrganizationalUnitTypeMultiplier(currentItem.id, {
                            multiplier: parseFloat(formData.multiplier)
                        });
                        const newData = await service.getAllOrganizationalUnitTypes();
                        setOrganizationalUnitTypes(newData);
                    }
                    break;

                case 'xportion':
                    if (modalMode === 'add') {
                        await XPortionService.create({
                            value: parseFloat(formData.value),
                            dateTime: new Date()
                        });
                    } else if (currentItem?.id) {
                        await XPortionService.update(currentItem.id, {
                            value: parseFloat(formData.value),
                            dateTime: currentItem.dateTime || new Date()
                        });
                    }
                    const newXPortionData = await XPortionService.getAll();
                    setXPortions(newXPortionData);
                    break;

                case 'nonorg':
                    if (modalMode === 'add') {
                        await NonOrgPositionService.create({
                            name: formData.name,
                            description: formData.description,
                            multiplier: parseFloat(formData.multiplier)
                        });
                    } else if (currentItem?.id) {
                        await NonOrgPositionService.update(currentItem.id, {
                            name: formData.name,
                            description: formData.description,
                            multiplier: parseFloat(formData.multiplier)
                        });
                    }
                    const newNonOrgData = await NonOrgPositionService.getAll();
                    setNonOrgPositions(newNonOrgData);
                    break;
            }

            handleCloseModal();
            toast.success(modalMode === 'add' ? 'Created successfully' : 'Updated successfully');
        } catch (error) {
            toast.error(modalMode === 'add' ? 'Failed to create' : 'Failed to update');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                const service = AppointmentParametersService;
                
                switch (key) {
                    case 'employment':
                        await service.deleteEmploymentType(id);
                        const newEmpData = await service.getAllEmploymentTypes();
                        setEmploymentTypes(newEmpData);
                        break;
                    case 'education':
                        await service.deleteEducationLevel(id);
                        const newEduData = await service.getAllEducationLevels();
                        setEducationLevels(newEduData);
                        break;
                    case 'experience':
                        await service.deleteExperienceLevel(id);
                        const newExpData = await service.getAllExperienceLevels();
                        setExperienceLevels(newExpData);
                        break;
                    case 'organizational':
                        await service.deleteOrganizationalLevel(id);
                        const newOrgLevelData = await service.getAllOrganizationalLevels();
                        setOrganizationalLevels(newOrgLevelData);
                        break;
                    case 'unit':
                        await service.deleteOrganizationalUnitType(id);
                        const newUnitData = await service.getAllOrganizationalUnitTypes();
                        setOrganizationalUnitTypes(newUnitData);
                        break;
                    case 'xportion':
                        await XPortionService.delete(id);
                        const newXPortionData = await XPortionService.getAll();
                        setXPortions(newXPortionData);
                        break;
                    case 'nonorg':
                        await NonOrgPositionService.delete(id);
                        const newNonOrgData = await NonOrgPositionService.getAll();
                        setNonOrgPositions(newNonOrgData);
                        break;
                }
                toast.success('Deleted successfully');
            } catch (error) {
                toast.error('Failed to delete');
            }
        }
    };

    // Get the correct ID field based on the current tab
    const getItemId = (item) => {
        if (key === 'experience') {
            return item.experienceLevelId || item.id;
        } else if (key === 'employment') {
            return item.employmentTypeId || item.id;
        } else if (key === 'education') {
            return item.educationLevelId || item.id;
        } else if (key === 'organizational') {
            return item.organizationalLevelId || item.id;
        } else if (key === 'unit') {
            return item.organizationalUnitTypeId || item.id;
        } else {
            return item.id;
        }
    };

    // Define table columns for each tab
    const getColumns = () => {
        if (key === 'xportion') {
            return [
                { 
                    key: 'dateTime', 
                    label: 'Date',
                    render: (value) => dateService.formatDate(value)
                },
                { key: 'value', label: 'Value' }
            ];
        } else {
            return [
                { key: 'name', label: 'Name' },
                { key: 'multiplier', label: 'Multiplier' },
                ...(key === 'nonorg' ? [{ key: 'description', label: 'Description' }] : [])
            ];
        }
    };

    // Define actions for each tab
    const getActions = () => {
        const actions = [
            {
                icon: <BsPencil />,
                variant: 'outline-primary',
                onClick: (item) => handleOpenModal('edit', item)
            }
        ];
        
        // Add delete button for all except unit type
        if (key !== 'unit') {
            actions.push({
                icon: <BsTrash />,
                variant: 'outline-danger',
                onClick: (item) => handleDelete(getItemId(item))
            });
        }
        
        return actions;
    };

    // Get the current data based on active tab
    const getCurrentData = () => {
        switch (key) {
            case 'employment': return employmentTypes;
            case 'education': return educationLevels;
            case 'experience': return experienceLevels;
            case 'organizational': return organizationalLevels;
            case 'unit': return organizationalUnitTypes;
            case 'xportion': return xPortions;
            case 'nonorg': return nonOrgPositions;
            default: return [];
        }
    };

    return (
        <div className="container mt-4">
            <Tabs
                activeKey={key}
                onSelect={(k) => setKey(k)}
                className="mb-3"
            >
                <Tab eventKey="employment" title="Employment Types">
                    <div className="mb-3">
                        <Button 
                            variant="primary" 
                            onClick={() => handleOpenModal('add')}
                        >
                            <BsPlusLg /> Add Employment Type
                        </Button>
                    </div>
                    <ResponsiveTable 
                        data={employmentTypes} 
                        columns={getColumns()} 
                        actions={getActions()} 
                    />
                </Tab>
                <Tab eventKey="education" title="Education Levels">
                    <div className="mb-3">
                        <Button 
                            variant="primary" 
                            onClick={() => handleOpenModal('add')}
                        >
                            <BsPlusLg /> Add Education Level
                        </Button>
                    </div>
                    <ResponsiveTable 
                        data={educationLevels} 
                        columns={getColumns()} 
                        actions={getActions()} 
                    />
                </Tab>
                <Tab eventKey="experience" title="Experience Levels">
                    <div className="mb-3">
                        <Button 
                            variant="primary" 
                            onClick={() => handleOpenModal('add')}
                        >
                            <BsPlusLg /> Add Experience Level
                        </Button>
                    </div>
                    <ResponsiveTable 
                        data={experienceLevels} 
                        columns={getColumns()} 
                        actions={getActions()} 
                    />
                </Tab>
                <Tab eventKey="organizational" title="Organizational Levels">
                    <div className="mb-3">
                        <Button 
                            variant="primary" 
                            onClick={() => handleOpenModal('add')}
                        >
                            <BsPlusLg /> Add Organizational Level
                        </Button>
                    </div>
                    <ResponsiveTable 
                        data={organizationalLevels} 
                        columns={getColumns()} 
                        actions={getActions()} 
                    />
                </Tab>
                <Tab eventKey="unit" title="Unit Type Multipliers">
                    <ResponsiveTable 
                        data={organizationalUnitTypes} 
                        columns={getColumns()} 
                        actions={getActions()} 
                    />
                </Tab>
                <Tab eventKey="xportion" title="X-Portion">
                    <div className="mb-3">
                        <Button 
                            variant="primary" 
                            onClick={() => handleOpenModal('add')}
                        >
                            <BsPlusLg /> Add X-Portion Value
                        </Button>
                    </div>
                    <ResponsiveTable 
                        data={xPortions} 
                        columns={getColumns()} 
                        actions={getActions()} 
                    />
                </Tab>
                <Tab eventKey="nonorg" title="Non-Organizational Positions">
                    <div className="mb-3">
                        <Button 
                            variant="primary" 
                            onClick={() => handleOpenModal('add')}
                        >
                            <BsPlusLg /> Add Non-Organizational Position
                        </Button>
                    </div>
                    <ResponsiveTable 
                        data={nonOrgPositions} 
                        columns={getColumns()} 
                        actions={getActions()} 
                    />
                </Tab>
            </Tabs>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {modalMode === 'add' ? 'Add New' : 'Edit'} {
                                key === 'employment' ? 'Employment Type' :
                                key === 'education' ? 'Education Level' :
                                key === 'experience' ? 'Experience Level' :
                                key === 'organizational' ? 'Organizational Level' :
                                key === 'unit' ? 'Unit Type Multiplier' :
                                key === 'xportion' ? 'X-Portion Value' :
                                'Non-Organizational Position'
                            }
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {key === 'xportion' ? (
                            <Form.Group className="mb-3">
                                <Form.Label>Value</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="value"
                                    value={formData.value}
                                    onChange={handleInputChange}
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    required
                                />
                            </Form.Group>
                        ) : (
                            <>
                                {key !== 'unit' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                maxLength={100}
                                            />
                                        </Form.Group>
                                        {key === 'nonorg' && (
                                            <Form.Group className="mb-3">
                                                <Form.Label>Description</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleInputChange}
                                                    maxLength={500}
                                                />
                                            </Form.Group>
                                        )}
                                    </>
                                )}
                                {(key === 'employment' || key === 'education' || key === 'experience' || key === 'organizational' || key === 'unit' || key === 'nonorg') && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Multiplier</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="multiplier"
                                            value={formData.multiplier}
                                            onChange={handleInputChange}
                                            step="0.1"
                                            min="0"
                                            max="10"
                                            required
                                        />
                                        {key === 'nonorg' && (
                                            <Form.Text className="text-muted">
                                                Position importance multiplier (default: 1.0)
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {modalMode === 'add' ? 'Add' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}